import { IAuditLogEdit } from "../Types"

// The freshest snapshot of a record (a Day/Log) to resolve the edit history's "current
// value" from (issue #1136). The `day`/`log` a view renders is owned far above it
// (TripView's `days`, FocusView's `currentState.days`, DayDataView's `logs`) and only
// catches up on the next live broadcast or full reload -- and that broadcast is merged
// in place by `AppData.updateData` (`Object.assign`), so the identity-keyed `useMemo`s
// computing the edit history don't even re-run for it. A save's own response therefore
// has to be applied directly, or a just-saved field's "new value" can keep rendering
// the pre-edit value (a dash, when the field was previously unset) until a reload.
//
// `saved` only wins while it's at least as fresh as `record`: once a genuinely newer
// copy arrives (e.g. another user's edit), this client's own stale snapshot steps
// aside rather than shadowing it.
export function liveRecord<T extends { id: number; updatedAt: string }>(record: T, saved: T | undefined): T {
  if (!saved || saved.id != record.id) {
    return record;
  }
  const savedAt = Date.parse(saved.updatedAt);
  const recordAt = Date.parse(record.updatedAt);
  if (!Number.isFinite(savedAt) || !Number.isFinite(recordAt)) {
    return saved;
  }
  return savedAt >= recordAt ? saved : record;
}

// Shared computation for a record's (Log/Day/Status) field-level edit history (issue
// #529), used by both the Log and Day edit-history UI. `AuditLogEdit` only stores
// `previousValue` per edit; a given edit's "new value" isn't stored anywhere -- it's
// always implicitly the *next* chronological edit's `previousValue` for that same
// field, or (for the latest edit of a field) the record's own current live value.
export interface EditHistoryEntry {
  field: string;
  previousValue: unknown;
  newValue: unknown;
  editedAt: string;
  shipUserId: number;
  // Every field changed together in a single logs.update/days.update call shares one
  // editGroupId (see `AuditLogEdit.editGroupId`); the server may also reuse it across
  // separate-but-nearby saves by the same user (issue #529 write-time grouping, see
  // `AuditLogEdit.reusableEditGroupId`) -- entries sharing a group therefore share the
  // same shipUserId, but not necessarily the same editedAt. Used by `groupEditHistory`
  // to collapse related edits back into a single edit-history row.
  editGroupId: string;
}

// `AuditLogEdit.previousValue` is a JSON-encoded string (or undefined when the field
// was previously unset). Falls back to the raw string for the rare case it isn't
// valid JSON, rather than throwing.
function decodeValue(value?: string): unknown {
  if (value === undefined) {
    return undefined;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

// Computes one entry per `AuditLogEdit`, across every field represented in `edits`.
// `currentValue(field)` supplies the record's own live value for a field's most
// recent edit (there's nothing "newer" recorded for it to diff against). Entries are
// grouped and chronologically ordered per field internally, then the flat result is
// sorted by `editedAt` descending (most recent edit first, regardless of field) --
// the order the Log/Day edit-history UI displays them in.
// `editedAt` alone isn't a reliable chronological order: two separate edits of the
// same field can land with an identical (millisecond) timestamp, e.g. under fast/
// automated saves. `id` is monotonically increasing on insert, so it always breaks
// a tie correctly -- ascending `id` therefore serves as the true chronological order
// whenever `editedAt` compares equal.
function compareChronological(a: IAuditLogEdit, b: IAuditLogEdit): number {
  return a.editedAt.localeCompare(b.editedAt) || a.id - b.id;
}

export function computeEditHistory(edits: IAuditLogEdit[], currentValue: (field: string) => unknown): EditHistoryEntry[] {
  return computeEditHistoryEntries(edits, currentValue).map(({ id: _id, ...entry }) => entry);
}

// As `computeEditHistory`, but keeps each entry's originating `AuditLogEdit.id`.
// The edit-history UI has no use for it (hence `computeEditHistory` dropping it),
// but a caller that needs to pick out specific rows does -- a day-edit log entry
// names the exact `AuditLogEdit`s it announces (see `logRosterChanges`, issue #1285).
export function computeEditHistoryEntries(edits: IAuditLogEdit[], currentValue: (field: string) => unknown): (EditHistoryEntry & { id: number })[] {
  const byField = new Map<string, IAuditLogEdit[]>();
  for (const edit of edits) {
    const fieldEdits = byField.get(edit.field) ?? [];
    fieldEdits.push(edit);
    byField.set(edit.field, fieldEdits);
  }

  const entries: (EditHistoryEntry & { id: number })[] = [];
  for (const [field, fieldEdits] of byField) {
    const sorted = [...fieldEdits].sort(compareChronological);
    sorted.forEach((edit, index) => {
      const next = sorted[index + 1];
      const newValue = next ? decodeValue(next.previousValue) : currentValue(field);
      entries.push({
        field,
        previousValue: decodeValue(edit.previousValue),
        newValue,
        editedAt: edit.editedAt,
        shipUserId: edit.shipUserId,
        editGroupId: edit.editGroupId,
        id: edit.id,
      });
    });
  }

  return entries.sort((a, b) => b.editedAt.localeCompare(a.editedAt) || b.id - a.id);
}

// One field's previous/new value within an `EditHistoryGroup` row.
export interface EditHistoryGroupField {
  field: string;
  previousValue: unknown;
  newValue: unknown;
}

// One row of the edit-history table (issue #529 UI redesign): every field changed
// together in a single logs.update/days.update call -- or across separate-but-nearby
// saves by the same user, when the server reuses the editGroupId (see
// `AuditLogEdit.reusableEditGroupId`) -- shares one editGroupId, so grouping by it
// turns what used to be N separate per-field rows into a single row with N stacked
// field/previous/new lines and one shared "changed" meta cell.
export interface EditHistoryGroup {
  editGroupId: string;
  editedAt: string;
  shipUserId: number;
  fields: EditHistoryGroupField[];
}

// Groups `computeEditHistory`'s flat, per-field entries by editGroupId, preserving
// the overall most-recent-first order -- a group's `editedAt` is taken from the first
// (i.e. most recent) entry seen for it, so it always reflects the group's latest
// activity even when the same editGroupId spans several saves. When the same field
// appears more than once within one group (possible now that a group can span
// multiple saves -- see `EditHistoryEntry.editGroupId`), the occurrences are merged
// into a single line rather than duplicated: since entries are processed most-recent
// first, the first occurrence's newValue is already correct/latest, and each further
// (chronologically earlier) occurrence only ever supplies an earlier previousValue.
export function groupEditHistory(entries: EditHistoryEntry[]): EditHistoryGroup[] {
  const groups: EditHistoryGroup[] = [];
  const byId = new Map<string, EditHistoryGroup>();
  for (const entry of entries) {
    let group = byId.get(entry.editGroupId);
    if (!group) {
      group = { editGroupId: entry.editGroupId, editedAt: entry.editedAt, shipUserId: entry.shipUserId, fields: [] };
      byId.set(entry.editGroupId, group);
      groups.push(group);
    }
    const existingField = group.fields.find((f) => f.field === entry.field);
    if (existingField) {
      existingField.previousValue = entry.previousValue;
    } else {
      group.fields.push({ field: entry.field, previousValue: entry.previousValue, newValue: entry.newValue });
    }
  }
  return groups;
}

// Renders a single edit-history value (previous/new) for display -- decoded JSON can
// be a string, number, array (e.g. Log's engineRpms or Day's crew/passengers), etc.
export function formatEditValue(value: unknown): string {
  if (value === undefined || value === null) {
    return "–";
  }
  if (Array.isArray(value)) {
    return value.join(", ");
  }
  return String(value);
}
